import { Box } from '@givebutter/primitives/box';
import * as SingleSelect from '@givebutter/primitives/single-select';
import * as Form from 'components/form';
import * as AuctionTooltip from 'components/pages/campaign-settings/campaign-auction/AuctionTooltip';
import * as NotificationSetting from 'components/pages/campaign-settings/campaign-auction/auction-communications/NotificationSetting';
import { useAuctionCommunications } from 'components/pages/campaign-settings/campaign-auction/auction-communications/useAuctionCommunications';
import { IntercomProductTourTargets } from 'constants/intercom';

const reminderCopyFactory = (value: string) =>
    `We'll send you a reminder 15 minutes before your auction ${value}.`;

const AuctionCommunications = () => {
    const { methods, onSubmit, isOutstandingInvoiceDropdownDisabled } = useAuctionCommunications();
    const { control } = methods;

    return (
        <Form.Provider {...methods}>
            <Box className={'flex flex-col space-y-6'}>
                <form className={'contents'} onSubmit={onSubmit}>
                    <div className={'flex flex-col gap-y-1 px-8 pb-5 pt-8'}>
                        <h2
                            data-intercom-target={
                                IntercomProductTourTargets.AuctionNotificationsTitle
                            }
                            className={'text-2xl font-medium'}
                        >
                            Bidder notifications
                        </h2>
                        <div className={'flex items-center space-x-2'}>
                            <p className={'text-base text-gray-600'}>
                                Select the information sent to bidders regarding your auction.
                            </p>
                            <AuctionTooltip.Root>
                                <AuctionTooltip.Trigger />
                                <AuctionTooltip.Content>
                                    Bidders control their own item-level notifications, i.e., when
                                    an item has been won, out bid, or a payment processed.
                                </AuctionTooltip.Content>
                            </AuctionTooltip.Root>
                        </div>
                    </div>
                    <div className={'divide-y divide-solid'}>
                        <div className={'grid w-full grid-cols-6 place-self-center px-8 py-5'}>
                            <div className={'col-span-1 col-start-5 flex flex-col items-center'}>
                                <p>SMS</p>
                            </div>
                            <div className={'col-span-1 place-self-center'}>
                                <p>Email</p>
                            </div>
                        </div>
                        <NotificationSetting.Root>
                            <NotificationSetting.Label>
                                <NotificationSetting.Text>Auction start</NotificationSetting.Text>
                                <NotificationSetting.Tooltip>
                                    {reminderCopyFactory('starts')}
                                </NotificationSetting.Tooltip>
                            </NotificationSetting.Label>
                            <NotificationSetting.Control>
                                <Form.Switch name={'startSms'} control={control} />
                            </NotificationSetting.Control>
                            <NotificationSetting.Control>
                                <Form.Switch name={'startEmail'} control={control} />
                            </NotificationSetting.Control>
                        </NotificationSetting.Root>
                        <NotificationSetting.Root>
                            <NotificationSetting.Label>
                                <NotificationSetting.Text>Auction end</NotificationSetting.Text>
                                <NotificationSetting.Tooltip>
                                    {reminderCopyFactory('ends')}
                                </NotificationSetting.Tooltip>
                            </NotificationSetting.Label>
                            <NotificationSetting.Control>
                                <Form.Switch name={'endSms'} control={control} />
                            </NotificationSetting.Control>
                            <NotificationSetting.Control>
                                <Form.Switch name={'endEmail'} control={control} />
                            </NotificationSetting.Control>
                        </NotificationSetting.Root>
                        <NotificationSetting.Root className="items-center">
                            <NotificationSetting.Label className="col-span-3">
                                <NotificationSetting.Text>
                                    Outstanding invoice
                                </NotificationSetting.Text>
                                <NotificationSetting.Tooltip>
                                    Send the winning bidder up to three reminders of an outstanding
                                    invoice.
                                </NotificationSetting.Tooltip>
                            </NotificationSetting.Label>
                            <NotificationSetting.Control className="col-span-1">
                                <Form.SingleSelect control={control} name="outstandingInvoice">
                                    <SingleSelect.Trigger
                                        disabled={isOutstandingInvoiceDropdownDisabled}
                                        aria-label="Outstanding invoice"
                                        size="sm"
                                    />
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="48">
                                            <SingleSelect.ItemText>2 Days</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="72">
                                            <SingleSelect.ItemText>3 Days</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="168">
                                            <SingleSelect.ItemText>7 Days</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                            </NotificationSetting.Control>
                            <NotificationSetting.Control>
                                <Form.Switch name={'outstandingInvoiceSmsOn'} control={control} />
                            </NotificationSetting.Control>
                            <NotificationSetting.Control>
                                <Form.Switch name={'outstandingInvoiceEmailOn'} control={control} />
                            </NotificationSetting.Control>
                        </NotificationSetting.Root>
                    </div>
                    <div className={'flex justify-end px-8 pb-8 pt-4'}>
                        <Form.SubmitButton className={'place-self-end'}>Save</Form.SubmitButton>
                    </div>
                </form>
            </Box>
        </Form.Provider>
    );
};

export { AuctionCommunications };
